import { useState, useEffect } from 'react';
import { flightService } from '../../services/flightService';
import BeforeYouBook from '../BeforeYouBook';
import { calculateFlightPricing } from '../../utils/flightPricing';

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/assets/images/no_aircraft.png';

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  return `${baseUrl}/${imageUrl}`;
};

const ReturnFlightDetails = ({ outboundFlightId, returnFlightId, passengers, onNext }) => {
  const [outboundFlight, setOutboundFlight] = useState(null);
  const [returnFlight, setReturnFlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOutboundBeforeYouBook, setShowOutboundBeforeYouBook] = useState(false);
  const [showReturnBeforeYouBook, setShowReturnBeforeYouBook] = useState(false);
  const [outboundaircraftImages, setOutboundAircraftImages] = useState([]);
  const [returnaircraftImages, setReturnAircraftImages] = useState([]);

  useEffect(() => {
    fetchFlightDetails();
  }, [outboundFlightId, returnFlightId]);

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);

      // Use the new API to fetch both flights in a single call
      const response = await flightService.getMultipleFlightDetails({
        outbound_flight_id: parseInt(outboundFlightId),
        return_flight_id: parseInt(returnFlightId || outboundFlightId)
      });

      console.log('Multiple flight details response:', response);

      if (response.success) {
        console.log('Outbound flight:', response.data.outbound);
        console.log('Return flight:', response.data.return);
        setOutboundFlight(response.data.outbound);
        setReturnFlight(response.data.return);
        setOutboundAircraftImages(response.data.outbound.aircraft_images || []);
        setReturnAircraftImages(response.data.return.aircraft_images || []);
      } else {
        setError('Failed to load flight details');
      }
    } catch (err) {
      console.error('Failed to load flight details:', err);
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDateTime = (datetime, format = 'time') => {
    const date = new Date(datetime);

    if (format === 'time') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } else if (format === 'date') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
      return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${year}`;
    } else if (format === 'full') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }
  };

  const calculateDuration = (flight) => {
    if (!flight) return { hours: 0, minutes: 0 };

    // Use duration_minutes from API if available
    if (flight.duration_minutes) {
      const hours = Math.floor(flight.duration_minutes / 60);
      const minutes = flight.duration_minutes % 60;
      return { hours, minutes };
    }

    // Fallback to calculation from departure/arrival times
    const departure = new Date(flight.departure.datetime);
    const arrival = new Date(flight.arrival.datetime);
    const diffMs = arrival - departure;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <img src="/assets/images/flight_logo.gif" alt="Loading..." />
      </div>
    );
  }

  if (error || !outboundFlight || !returnFlight) {
    return (
      <div className="container mx-auto px-4 mt-5">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Flight details not found'}
        </div>
      </div>
    );
  }

  const outboundPricing = calculateFlightPricing(outboundFlight, passengers);
  const returnPricing = calculateFlightPricing(returnFlight, passengers);
  const outboundDuration = calculateDuration(outboundFlight);
  const returnDuration = calculateDuration(returnFlight);
  const totalOutboundFare = outboundPricing.totalFare || outboundPricing.blockFare;
  const totalReturnFare = returnPricing.totalFare || returnPricing.blockFare;
  const grandTotal = totalOutboundFare + totalReturnFare;

  return (
    <section className="py-6 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left Column - Flight Cards and Summary */}
          <div className="lg:col-span-3 space-y-3">
            {/* Outbound Flight Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="container mx-auto px-4 pt-3 pb-3">
                <div className="flex flex-wrap -mx-4">
                  {/* Aircraft Image */}
                  <div className="w-full xl:w-1/4 md:w-1/3 px-4">
                    <div className="w-full">
                      {outboundaircraftImages.length > 0 ? (
                        <img
                          src={getImageUrl(outboundaircraftImages[0].presigned_url || outboundaircraftImages[0].url)}
                          alt={outboundaircraftImages[0].name}
                          className="w-full h-auto object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.src = '/assets/images/no_aircraft.png';
                          }}
                        />
                      ) : (
                        <img
                          src="/assets/images/no_aircraft.png"
                          alt="Aircraft"
                          className="w-full h-auto object-cover rounded"
                        />
                      )}
                    </div>
                  </div>

                  {/* Flight Details */}
                  <div className="w-full xl:w-3/4 md:w-2/3 px-4">
                    <div className="pt-2">
                      <div className="py-2">
                        <p className="text-lg text-gray-800 font-bold m-0">
                          {outboundFlight.flight_number?.toUpperCase()}
                        </p>
                        <div className="py-2">
                          <div className="flex flex-wrap -mx-4">
                            <div className="w-full px-4">
                              <div className="flex flex-wrap -mx-4">
                                <div className="w-full md:w-1/3 px-4">
                                  <h4 className="text-sm font-bold text-gray-600 mb-0">
                                    {outboundFlight.departure.airport_code} {formatDateTime(outboundFlight.departure.datetime, 'time')}
                                  </h4>
                                  <p className="mb-0">
                                    {formatDateTime(outboundFlight.departure.datetime, 'date')}
                                  </p>
                                </div>

                                <div className="w-full md:w-1/3 px-4 text-center">
                                  <span>→</span>
                                  <p className="mb-0">{outboundDuration.hours} Hrs {outboundDuration.minutes} Mins</p>
                                </div>

                                <div className="w-full md:w-1/3 px-4 text-right">
                                  <h4 className="text-sm font-bold text-gray-600 mb-0">
                                    {outboundFlight.arrival.airport_code} {formatDateTime(outboundFlight.arrival.datetime, 'time')}
                                  </h4>
                                  <p className="mb-0">
                                    {formatDateTime(outboundFlight.arrival.datetime, 'date')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap -mx-4">
                            <div className="w-full md:w-7/12 px-4">
                              <div className="flex gap-4 mt-2">
                                <p className="text-sm mb-0">Available Seats: <span className="font-bold">{outboundFlight.availableSeats}</span></p>
                              </div>
                              <p className="mb-0 font-bold text-black-600 text-sm">
                                Fare/Pax ${formatCurrency(outboundPricing.farePerPax)}
                                &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                Total Fare ${formatCurrency(totalOutboundFare)}
                              </p>
                            </div>
                            <div style={{ paddingTop: '20px' }} className="w-full md:w-5/12 px-4 text-right">
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setShowOutboundBeforeYouBook(!showOutboundBeforeYouBook);
                                }}
                                className="font-bold text-gray-600 text-sm no-underline"
                              >
                                Before You Book
                                <i className={`fa fa-caret-${showOutboundBeforeYouBook ? 'up' : 'down'} ml-2`}></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Before You Book Section for Outbound */}
            <BeforeYouBook
              isOpen={showOutboundBeforeYouBook}
              onToggle={() => setShowOutboundBeforeYouBook(!showOutboundBeforeYouBook)}
            />

            {/* Return Flight Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="container mx-auto px-4 pt-3 pb-3">
                <div className="flex flex-wrap -mx-4">
                  {/* Aircraft Image */}
                  <div className="w-full xl:w-1/4 md:w-1/3 px-4">
                    <div className="w-full">
                      {returnaircraftImages.length > 0 ? (
                        <img
                          src={getImageUrl(returnaircraftImages[0].presigned_url || returnaircraftImages[0].url)}
                          alt={returnaircraftImages[0].name}
                          className="w-full h-auto object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.src = '/assets/images/no_aircraft.png';
                          }}
                        />
                      ) : (
                        <img
                          src="/assets/images/no_aircraft.png"
                          alt="Aircraft"
                          className="w-full h-auto object-cover rounded"
                        />
                      )}
                    </div>
                  </div>

                  {/* Flight Details */}
                  <div className="w-full xl:w-3/4 md:w-2/3 px-4">
                    <div className="pt-2">
                      <div className="py-2">
                        <p className="text-lg text-gray-800 font-bold m-0">
                          {returnFlight.flight_number?.toUpperCase()}
                        </p>
                        <div className="py-2">
                          <div className="flex flex-wrap -mx-4">
                            <div className="w-full px-4">
                              <div className="flex flex-wrap -mx-4">
                                <div className="w-full md:w-1/3 px-4">
                                  <h4 className="text-sm font-bold text-gray-600 mb-0">
                                    {returnFlight.departure.airport_code} {formatDateTime(returnFlight.departure.datetime, 'time')}
                                  </h4>
                                  <p className="mb-0">
                                    {formatDateTime(returnFlight.departure.datetime, 'date')}
                                  </p>
                                </div>

                                <div className="w-full md:w-1/3 px-4 text-center">
                                  <span>→</span>
                                  <p className="mb-0">{returnDuration.hours} Hrs {returnDuration.minutes} Mins</p>
                                </div>

                                <div className="w-full md:w-1/3 px-4 text-right">
                                  <h4 className="text-sm font-bold text-gray-600 mb-0">
                                    {returnFlight.arrival.airport_code} {formatDateTime(returnFlight.arrival.datetime, 'time')}
                                  </h4>
                                  <p className="mb-0">
                                    {formatDateTime(returnFlight.arrival.datetime, 'date')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap -mx-4">
                            <div className="w-full md:w-7/12 px-4">
                              <div className="flex gap-4 mt-2">
                                <p className="text-sm mb-0">Available Seats: <span className="font-bold">{returnFlight.availableSeats}</span></p>
                              </div>
                              <p className="mb-0 font-bold text-black-600 text-sm">
                                Fare/Pax ${formatCurrency(returnPricing.farePerPax)}
                                &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                Total Fare ${formatCurrency(totalReturnFare)}
                              </p>
                            </div>
                            <div style={{ paddingTop: '20px' }} className="w-full md:w-5/12 px-4 text-right">
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setShowReturnBeforeYouBook(!showReturnBeforeYouBook);
                                }}
                                className="font-bold text-gray-600 text-sm no-underline"
                              >
                                Before You Book
                                <i className={`fa fa-caret-${showReturnBeforeYouBook ? 'up' : 'down'} ml-2`}></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Before You Book Section for Return */}
            <BeforeYouBook
              isOpen={showReturnBeforeYouBook}
              onToggle={() => setShowReturnBeforeYouBook(!showReturnBeforeYouBook)}
            />

            {/* Flight Summary Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-yellow-400 px-4 py-2">
                <h3 className="font-bold text-gray-800 text-sm">Flight Summary</h3>
              </div>
              <div className="overflow-x-auto px-2">
                <table className="w-full text-xs" style={{ border: '1px solid #dee2e6' }}>
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50" style={{ border: '1px solid #dee2e6' }}>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>Fl. No.</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>Dep.</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>ETD</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>Arr.</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>ETA</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>Flight Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-gray-50" style={{ border: '1px solid #dee2e6' }}>
                      <td className="px-3 py-2 text-gray-800 font-medium" style={{ border: '1px solid #dee2e6' }}>{outboundFlight.flight_number?.toUpperCase()}</td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>{outboundFlight.departure.airport_code}</td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                        {formatDateTime(outboundFlight.departure.datetime, 'full')}
                      </td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>{outboundFlight.arrival.airport_code}</td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                        {formatDateTime(outboundFlight.arrival.datetime, 'full')}
                      </td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                        {outboundDuration.hours}h {outboundDuration.minutes}m
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50" style={{ border: '1px solid #dee2e6' }}>
                      <td className="px-3 py-2 text-gray-800 font-medium" style={{ border: '1px solid #dee2e6' }}>{returnFlight.flight_number?.toUpperCase()}</td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>{returnFlight.departure.airport_code}</td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                        {formatDateTime(returnFlight.departure.datetime, 'full')}
                      </td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>{returnFlight.arrival.airport_code}</td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                        {formatDateTime(returnFlight.arrival.datetime, 'full')}
                      </td>
                      <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                        {returnDuration.hours}h {returnDuration.minutes}m
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column - Fare Box */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm sticky top-4 overflow-hidden">
              {/* Fare Header */}
              <div className="bg-yellow-400 px-4 py-3">
                <h3 className="font-bold text-gray-800 text-lg">Fare</h3>
              </div>

              {/* Fare Content */}
              <div className="p-4 space-y-4">
                {/* Outbound Pricing */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-bold text-gray-700">{outboundFlight.departure.airport_code}</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-sm font-bold text-gray-700">{outboundFlight.arrival.airport_code}</span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Passenger(s)</span>
                      <span className="text-gray-700 font-semibold">{passengers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Fare/Pax</span>
                      <span className="text-gray-700 font-semibold">
                        ${formatCurrency(outboundPricing.farePerPax)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Fare</span>
                      <span className="text-gray-700 font-semibold">
                        ${formatCurrency(totalOutboundFare)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t-2 border-dashed border-gray-300"></div>

                {/* Return Pricing */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-bold text-gray-700">{returnFlight.departure.airport_code}</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-sm font-bold text-gray-700">{returnFlight.arrival.airport_code}</span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Passenger(s)</span>
                      <span className="text-gray-700 font-semibold">{passengers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Fare/Pax</span>
                      <span className="text-gray-700 font-semibold">
                        ${formatCurrency(returnPricing.farePerPax)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Fare</span>
                      <span className="text-gray-700 font-semibold">
                        ${formatCurrency(totalReturnFare)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t-2 border-dashed border-gray-300"></div>

                {/* Grand Total */}
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-bold">Total Passenger(s)</span>
                    <span className="text-gray-700 font-bold">{passengers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-bold">Grand Total</span>
                    <span className="text-gray-700 font-bold">
                      ${formatCurrency(grandTotal)}
                    </span>
                  </div>
                </div>

                {/* Next Button */}
                <button
                  className="w-full px-4 py-3 bg-yellow-400 text-gray-800 font-bold rounded hover:bg-yellow-300 transition-colors text-sm mt-4"
                  onClick={onNext}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReturnFlightDetails;
